import { useMessages, useTranslations } from 'next-intl';
import { Fragment } from 'react';

import { MaterialDesignIcon } from '@hrworks/sui-core/MaterialDesignIcon';
import { Text } from '@hrworks/sui-core/Text';

import { ExternalLink } from '../../Link';
import { SidebarSection } from '../SidebarSection';
import type { SidebarEstablishmentsProps } from './SidebarEstablishments.types';

const buildGoogleMapsUrl = (address: {
  street?: string;
  zipCode?: string;
  city?: string;
  internationalCountryCode?: string;
}) => {
  const parts = [address.street, address.zipCode, address.city, address.internationalCountryCode].filter(Boolean);
  const query = parts.join(', ');
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
};

export const SidebarEstablishments = ({
  permanentEstablishments,
  placeOfWorkTypeKey,
  remoteWorkName,
  ...otherProps
}: SidebarEstablishmentsProps) => {
  const t = useTranslations('Job');
  const messages = useMessages();
  const countries = (messages.Countries ?? {}) as Record<string, string>;

  // Remote- und Hybrid-Stellen bekommen die Arbeitsform als zusätzlichen Standort. Wording wie
  // in der JobPost-Card und den Suchoptionen: remoteWorkName ist ein Select-Key, "workRemotely"
  // hat eine eigene Übersetzung, "homeoffice" nutzt placeOfWorkTypeKey.remote ("Homeoffice").
  const showPlaceOfWork = placeOfWorkTypeKey && placeOfWorkTypeKey !== 'onsite';
  const placeOfWorkLabel =
    placeOfWorkTypeKey === 'remote' && remoteWorkName === 'workRemotely'
      ? t('remoteWorkNameKey.workRemotely')
      : placeOfWorkTypeKey && t(`placeOfWorkTypeKey.${placeOfWorkTypeKey}`);

  // Die API liefert fuer nicht gepflegte Adressfelder leere Strings. Nur gefuellte Zeilen
  // rendern, sonst stehen Leerzeilen ueber dem Land.
  const establishments = (permanentEstablishments ?? [])
    .map((permanentEstablishment) => {
      const { address } = permanentEstablishment;
      const code = address?.internationalCountryCode;

      return {
        permanentEstablishment,
        lines: [
          address?.street?.trim(),
          [address?.zipCode?.trim(), address?.city?.trim()].filter(Boolean).join(' '),
          code ? (countries[code] ?? code) : '',
        ].filter(Boolean),
      };
    })
    .filter(({ lines }) => lines.length > 0);

  if (establishments.length === 0 && !showPlaceOfWork) {
    return;
  }

  return (
    <SidebarSection title={t('locations')} {...otherProps}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {establishments.map(({ permanentEstablishment, lines }) => {
          const { address } = permanentEstablishment;
          const code = address?.internationalCountryCode;
          const hasAddress = address?.street || address?.city;
          const key = permanentEstablishment.id || `${address?.street}-${address?.zipCode}-${address?.city}`;

          const content = (
            <Text key={key}>
              {lines.map((line, index) => (
                <Fragment key={line}>
                  {index > 0 && <br />}
                  {line}
                </Fragment>
              ))}
            </Text>
          );

          if (hasAddress) {
            return (
              <ExternalLink
                key={key}
                href={buildGoogleMapsUrl({
                  street: address?.street,
                  zipCode: address?.zipCode,
                  city: address?.city,
                  internationalCountryCode: code,
                })}
                target="_blank"
                rel="noopener noreferrer"
              >
                {content}
              </ExternalLink>
            );
          }

          return content;
        })}
        {showPlaceOfWork && (
          <Text
            data-guide-id="sidebar-location-place-of-work"
            style={{ display: 'flex', alignItems: 'center', gap: 4 }}
          >
            <MaterialDesignIcon name="home" />
            {placeOfWorkLabel}
          </Text>
        )}
      </div>
    </SidebarSection>
  );
};
